import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Loader2, FileText, Download, Maximize2 } from 'lucide-react';
import { nactiPdfjs } from '../../lib/pdfjs';
import DokladNahledModal from './DokladNahledModal';

const stahnoutUrl = (dokladId, p) => `/api/finance/doklady/${dokladId}/prilohy/${p.id}/stahnout`;

/**
 * Náhled dokladu s listováním. Po normalizaci je doklad jedno PDF → PdfPager
 * (pdfjs, listování stran). Legacy (víc obrázků, než proběhne backfill) →
 * galerie obrázků. Jinak fallback (iframe/img).
 *
 * Ťuknutím se doklad otevře ve velkém (`DokladNahledModal`) a dá se přiblížit.
 * Ve sloupci je náhled široký pár set pixelů — na kontrolu, že je doklad od
 * koho má být, to stačí, na přečtení variabilního symbolu nebo rozpisu DPH ne.
 */
export default function DokladNahled({ prilohy = [], dokladId }) {
    const [zvetseno, setZvetseno] = useState(false);

    if (!prilohy.length) return null;

    const jenObrazky = prilohy.every(p => p.mime_type?.startsWith('image/'));

    // Sloučené PDF je po normalizaci jediná příloha, ale u starších dokladů
    // (a dokud neproběhne backfill) leží PDF vedle původních obrázků. Náhled
    // musí ukázat dokument, ne první řádek v tabulce — dřív padl na fallback
    // s `prilohy[0]`, což býval JPG jedné stránky.
    const pdf = prilohy.find(p => p.mime_type === 'application/pdf');

    const otevri = () => setZvetseno(true);

    return (
        <>
            {pdf
                ? <PdfPager url={stahnoutUrl(dokladId, pdf)} onZvetsit={otevri} />
                : jenObrazky
                    ? <ObrazkovaGalerie prilohy={prilohy} dokladId={dokladId} onZvetsit={otevri} />
                    : <FallbackPreview priloha={prilohy[0]} dokladId={dokladId} />}

            {zvetseno && (
                <DokladNahledModal prilohy={prilohy} dokladId={dokladId}
                    onClose={() => setZvetseno(false)} />
            )}
        </>
    );
}

/**
 * Obal, který z náhledu dělá tlačítko „otevři ve velkém".
 *
 * Je to `<button>`, ne `<div onClick>`: na doklad se dostane i ten, kdo jede
 * po klávesnici, a čtečka řekne, co se stane. Nápis se ukazuje až na najetí,
 * aby přes malý náhled nepřekážel pořád.
 */
function Zvetsovaci({ onZvetsit, children, popis = 'Otevřít ve velkém' }) {
    const [nad, setNad] = useState(false);

    if (!onZvetsit) return children;

    return (
        <button type="button" onClick={onZvetsit} title={popis} aria-label={popis}
            onMouseEnter={() => setNad(true)} onMouseLeave={() => setNad(false)}
            onFocus={() => setNad(true)} onBlur={() => setNad(false)}
            style={{
                display: 'block', width: '100%', padding: 0, border: 'none',
                background: 'transparent', cursor: 'zoom-in', position: 'relative',
                WebkitTapHighlightColor: 'transparent',
            }}>
            {children}
            <span style={{
                position: 'absolute', right: 8, top: 8,
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '5px 9px', borderRadius: 999,
                background: 'rgba(0,0,0,0.62)', color: '#fff',
                fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                opacity: nad ? 1 : 0, transition: 'opacity 0.15s ease-out',
                pointerEvents: 'none',
            }}>
                <Maximize2 size={12} /> Zvětšit
            </span>
        </button>
    );
}

// ── PDF listování přes pdfjs (canvas, fit na šířku) ──────────────────────────
function PdfPager({ url, onZvetsit }) {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const renderTaskRef = useRef(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [rendering, setRendering] = useState(false);
    const [chyba, setChyba] = useState(null);

    useEffect(() => {
        let cancelled = false;
        // Nová adresa = čistý start. Bez tohohle zůstala chyba viset napořád:
        // stačil jeden neúspěšný pokus (viz `DokladPanelSloupec` — přechodná
        // kombinace nového dokladu se starou přílohou vracela 404) a hláška
        // přebila i dokument, který se vzápětí načetl v pořádku. Vypadalo to
        // jako rozbitý náhled, a přitom stačilo obnovit stránku.
        setChyba(null);
        setPdfDoc(null);
        setTotalPages(0);
        (async () => {
            try {
                const pdfjsLib = await nactiPdfjs();
                const doc = await pdfjsLib.getDocument(url).promise;
                if (!cancelled) { setPdfDoc(doc); setTotalPages(doc.numPages); setCurrentPage(1); }
            } catch (err) {
                console.error('PDF load failed:', err);
                // Důvod patří na obrazovku, ne jen do konzole: „nepodařilo se
                // načíst" je hláška, s kterou nikdo nic neudělá. Chybějící soubor
                // (404) i odepřený přístup (403) se řeší úplně jinak.
                if (!cancelled) {
                    setChyba(err?.status === 404 ? 'Soubor přílohy na serveru chybí.'
                        : err?.status === 403 ? 'K této příloze nemáte přístup.'
                        : err?.message || 'Neznámá chyba.');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [url]);

    useEffect(() => {
        if (!pdfDoc || !canvasRef.current) return;
        let cancelled = false;
        setRendering(true);
        (async () => {
            try {
                const page = await pdfDoc.getPage(currentPage);
                const canvas = canvasRef.current;
                if (!canvas || cancelled) return;
                const ctx = canvas.getContext('2d');
                const containerWidth = containerRef.current?.clientWidth || 560;
                const dpr = window.devicePixelRatio || 1;
                const base = page.getViewport({ scale: 1 });
                const scale = containerWidth / base.width;
                const viewport = page.getViewport({ scale: scale * dpr * 1.5 });
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                canvas.style.width = `${containerWidth}px`;
                canvas.style.height = `${base.height * scale}px`;
                if (renderTaskRef.current) { try { renderTaskRef.current.cancel(); } catch { /* noop */ } }
                const task = page.render({ canvasContext: ctx, viewport });
                renderTaskRef.current = task;
                await task.promise;
            } catch (err) {
                if (err?.name !== 'RenderingCancelledException') console.error(err);
            }
            if (!cancelled) setRendering(false);
        })();
        return () => { cancelled = true; };
    }, [pdfDoc, currentPage]);

    if (chyba) {
        return (
            <div className="text-center py-10 px-4">
                <FileText size={22} className="mx-auto mb-2" style={{ color: 'var(--text-tertiary)' }} />
                <div className="text-sm" style={{ color: 'var(--text-primary)' }}>Náhled PDF se nepodařilo načíst</div>
                <div className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>{chyba}</div>
                <a href={url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg text-xs font-semibold"
                    style={{ color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}>
                    <Download size={13} /> Otevřít soubor
                </a>
            </div>
        );
    }
    if (!pdfDoc) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                <span className="ml-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>Načítám PDF…</span>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="w-full">
            {/* Klikací je jen dokument — listovací tlačítka pod ním musí zůstat
                svoje, jinak by každé přetočení stránky otevřelo modal. */}
            <Zvetsovaci onZvetsit={onZvetsit}>
                <div className="relative rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-default)' }}>
                    <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: 'auto' }} />
                    {rendering && (
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.08)', pointerEvents: 'none' }}>
                            <Loader2 size={18} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                        </div>
                    )}
                </div>
            </Zvetsovaci>
            {totalPages > 1 && <Pager page={currentPage} total={totalPages} onChange={setCurrentPage} />}
        </div>
    );
}

// ── Galerie obrázků (legacy, než proběhne backfill na PDF) ───────────────────
function ObrazkovaGalerie({ prilohy, dokladId, onZvetsit }) {
    const [idx, setIdx] = useState(0);
    const p = prilohy[Math.min(idx, prilohy.length - 1)];
    return (
        <div className="w-full">
            <Zvetsovaci onZvetsit={onZvetsit}>
                <img src={stahnoutUrl(dokladId, p)} alt={p.puvodni_nazev}
                    className="w-full rounded-lg" style={{ maxHeight: '600px', objectFit: 'contain', border: '1px solid var(--border-default)' }} />
            </Zvetsovaci>
            {prilohy.length > 1 && <Pager page={idx + 1} total={prilohy.length} onChange={(n) => setIdx(n - 1)} />}
        </div>
    );
}

function FallbackPreview({ priloha, dokladId }) {
    const url = stahnoutUrl(dokladId, priloha);
    if (priloha.mime_type === 'application/pdf') {
        return <iframe src={url} className="w-full rounded-lg" style={{ height: '600px', border: '1px solid var(--border-default)' }} title={priloha.puvodni_nazev} />;
    }
    if (priloha.mime_type?.startsWith('image/')) {
        return <img src={url} alt={priloha.puvodni_nazev} className="w-full rounded-lg" style={{ maxHeight: '600px', objectFit: 'contain', border: '1px solid var(--border-default)' }} />;
    }
    return (
        <div className="flex items-center justify-center py-12 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            <FileText size={20} className="mr-2" /> Neznámý typ souboru: {priloha.mime_type}
        </div>
    );
}

function Pager({ page, total, onChange }) {
    const btn = (disabled) => ({
        width: 32, height: 32, borderRadius: 6, border: '1px solid var(--border-default)',
        background: disabled ? 'transparent' : 'var(--surface-raised)',
        color: disabled ? 'var(--text-tertiary)' : 'var(--text-primary)',
        cursor: disabled ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    });
    return (
        <div className="flex items-center justify-center gap-3 pt-2">
            <button onClick={() => onChange(Math.max(1, page - 1))} disabled={page <= 1} style={btn(page <= 1)}>
                <ChevronLeft size={16} />
            </button>
            <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)', minWidth: 56, textAlign: 'center' }}>
                {page} / {total}
            </span>
            <button onClick={() => onChange(Math.min(total, page + 1))} disabled={page >= total} style={btn(page >= total)}>
                <ChevronRight size={16} />
            </button>
        </div>
    );
}
